import { useCallback, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useGetDefectPill } from '../hooks/useGetDefectPill';
import { abyssColors } from '@/theme/colors';

export const AssemblyNavigation = () => {
  const defectPill = useGetDefectPill();

  const allAssemblies = useAtomValue(state.allAssembliesForReview);
  const [selectedAssemblyId, setSelectedAssemblyId] = useAtom(state.selectedAssemblyId);
  const [selectedAssemblyName, setSelectedAssemblyName] = useAtom(state.selectedAssemblyName);

  const handlePrevious = useCallback(() => {
    if (!allAssemblies || allAssemblies.length === 0) return;

    const currentIndex = allAssemblies.findIndex((assembly) => assembly?.id === selectedAssemblyId);

    const newIndex = (currentIndex - 1 + allAssemblies.length) % allAssemblies.length;
    const newAssembly = allAssemblies[newIndex];

    setSelectedAssemblyId(newAssembly.id);
    setSelectedAssemblyName(newAssembly.tagName);
  }, [allAssemblies, selectedAssemblyId, setSelectedAssemblyId, setSelectedAssemblyName]);

  const handleNext = useCallback(() => {
    if (!allAssemblies || allAssemblies.length === 0) return;

    const currentIndex = allAssemblies.findIndex((a) => a.id === selectedAssemblyId);

    const newIndex = (currentIndex + 1) % allAssemblies.length;
    const newAssembly = allAssemblies[newIndex];

    setSelectedAssemblyId(newAssembly.id);
    setSelectedAssemblyName(newAssembly.tagName);
  }, [allAssemblies, selectedAssemblyId, setSelectedAssemblyId, setSelectedAssemblyName]);

  const isDisabled = useMemo(() => {
    return allAssemblies && allAssemblies?.length === 1;
  }, [allAssemblies]);

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Tooltip
        title={isDisabled ? 'Only one component available' : ''}
        disableHoverListener={!isDisabled}
        arrow
        placement="bottom"
      >
        <span>
          <IconButton onClick={handlePrevious} disabled={isDisabled}>
            <ArrowBackIosIcon
              sx={{
                color: isDisabled ? abyssColors.neutral[400] : abyssColors.primary[500],
                height: '2rem',
              }}
            />
          </IconButton>
        </span>
      </Tooltip>
      <Typography variant="h3" color="primary.700" fontWeight={700}>
        {selectedAssemblyName}
      </Typography>
      <Tooltip
        title={isDisabled ? 'Only one component available' : ''}
        disableHoverListener={!isDisabled}
        arrow
        placement="bottom"
      >
        <span>
          <IconButton onClick={handleNext} disabled={isDisabled}>
            <ArrowForwardIosIcon
              sx={{
                color: isDisabled ? abyssColors.neutral[400] : abyssColors.primary[500],
                height: '2rem',
              }}
            />
          </IconButton>
        </span>
      </Tooltip>
      {defectPill}
    </Stack>
  );
};
